import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../components/feature/NavBar';
import Button from '../../../components/base/Button';

interface Player {
  id: string;
  name: string;
  position: number;
  color: string;
  isTeacher: boolean;
}

interface GameState {
  players: Player[];
  currentPlayer: number;
  diceValue: number;
  isRolling: boolean;
  gameStarted: boolean;
  winner: Player | null;
  gameId: string;
  movingPlayer: string | null;
  difficulty: string;
}

export default function SnakesLadders() {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState>({
    players: [],
    currentPlayer: 0,
    diceValue: 1,
    isRolling: false,
    gameStarted: false,
    winner: null,
    gameId: '',
    movingPlayer: null,
    difficulty: 'kindergarten'
  });
  const [currentWord, setCurrentWord] = useState<string>('');
  const [showWordModal, setShowWordModal] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [isTeacher, setIsTeacher] = useState(false);
  const [gameId, setGameId] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [showGameCodeModal, setShowGameCodeModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState('kindergarten');
  
  // Player colors for better visibility
  const playerColors = [
    { bg: 'bg-red-500', border: 'border-red-600', name: 'Red' },
    { bg: 'bg-blue-500', border: 'border-blue-600', name: 'Blue' },
    { bg: 'bg-green-500', border: 'border-green-600', name: 'Green' },
    { bg: 'bg-yellow-500', border: 'border-yellow-600', name: 'Yellow' },
    { bg: 'bg-purple-500', border: 'border-purple-600', name: 'Purple' },
    { bg: 'bg-pink-500', border: 'border-pink-600', name: 'Pink' }
  ];
  
  // Enhanced Snakes and Ladders positions for 40-square board
  const snakes = { 
    32: 10, 19: 7, 25: 9, 27: 1, 35: 22, 39: 18
  };
  const ladders = { 
    3: 16, 6: 23, 11: 26, 15: 34, 20: 29, 24: 33
  };
  
  // Difficulty levels with age-appropriate words
  const difficultyLevels = {
    kindergarten: {
      name: 'Kindergarten',
      description: 'Simple 2-3 letter words',
      words: {
        1: 'at', 2: 'it', 3: 'up', 4: 'go', 5: 'me', 6: 'we', 7: 'to', 8: 'am', 9: 'in', 10: 'on',
        11: 'my', 12: 'is', 13: 'he', 14: 'be', 15: 'no', 16: 'so', 17: 'do', 18: 'an', 19: 'as', 20: 'or',
        21: 'if', 22: 'of', 23: 'by', 24: 'us', 25: 'hi', 26: 'ox', 27: 'ad', 28: 'ah', 29: 'aw', 30: 'ay',
        31: 'ax', 32: 'ex', 33: 'oh', 34: 'oy', 35: 'ya', 36: 'yo', 37: 'ah', 38: 'eh', 39: 'um', 40: 'uh'
      }
    },
    year1: {
      name: 'Year 1',
      description: 'CVC words and basic sight words',
      words: {
        1: 'cat', 2: 'bat', 3: 'hat', 4: 'mat', 5: 'rat', 6: 'sat', 7: 'fat', 8: 'pat', 9: 'vat', 10: 'flat',
        11: 'dog', 12: 'log', 13: 'fog', 14: 'jog', 15: 'hog', 16: 'bog', 17: 'cog', 18: 'frog', 19: 'clog', 20: 'blog',
        21: 'sun', 22: 'run', 23: 'fun', 24: 'gun', 25: 'bun', 26: 'nun', 27: 'pun', 28: 'spun', 29: 'stun', 30: 'shun',
        31: 'bed', 32: 'red', 33: 'led', 34: 'fed', 35: 'wed', 36: 'shed', 37: 'bred', 38: 'fled', 39: 'sled', 40: 'dread'
      }
    },
    year2: {
      name: 'Year 2',
      description: 'Consonant blends and digraphs',
      words: {
        1: 'ship', 2: 'shop', 3: 'shot', 4: 'shut', 5: 'shed', 6: 'shell', 7: 'shelf', 8: 'shine', 9: 'share', 10: 'shark',
        11: 'chip', 12: 'chat', 13: 'chin', 14: 'chop', 15: 'chest', 16: 'check', 17: 'chair', 18: 'chain', 19: 'charm', 20: 'champ',
        21: 'this', 22: 'that', 23: 'them', 24: 'then', 25: 'thin', 26: 'thick', 27: 'think', 28: 'thank', 29: 'three', 30: 'throw',
        31: 'when', 32: 'what', 33: 'where', 34: 'which', 35: 'white', 36: 'whale', 37: 'wheat', 38: 'wheel', 39: 'while', 40: 'whisk'
      }
    },
    year3: {
      name: 'Year 3',
      description: 'Long vowel sounds and magic e',
      words: {
        1: 'make', 2: 'take', 3: 'cake', 4: 'bake', 5: 'lake', 6: 'wake', 7: 'rake', 8: 'sake', 9: 'fake', 10: 'stake',
        11: 'time', 12: 'fine', 13: 'line', 14: 'mine', 15: 'nine', 16: 'vine', 17: 'pine', 18: 'dine', 19: 'wine', 20: 'spine',
        21: 'hope', 22: 'rope', 23: 'cope', 24: 'mope', 25: 'pope', 26: 'slope', 27: 'scope', 28: 'phone', 29: 'stone', 30: 'drone',
        31: 'cute', 32: 'mute', 33: 'tube', 34: 'cube', 35: 'huge', 36: 'fuse', 37: 'tune', 38: 'dune', 39: 'prune', 40: 'flute'
      }
    },
    year4: {
      name: 'Year 4',
      description: 'Complex words and suffixes',
      words: {
        1: 'jumping', 2: 'running', 3: 'walking', 4: 'talking', 5: 'playing', 6: 'working', 7: 'reading', 8: 'writing', 9: 'singing', 10: 'dancing',
        11: 'teacher', 12: 'student', 13: 'painter', 14: 'builder', 15: 'driver', 16: 'helper', 17: 'worker', 18: 'farmer', 19: 'baker', 20: 'maker',
        21: 'quickly', 22: 'slowly', 23: 'loudly', 24: 'softly', 25: 'badly', 26: 'madly', 27: 'sadly', 28: 'gladly', 29: 'kindly', 30: 'boldly',
        31: 'careful', 32: 'helpful', 33: 'useful', 34: 'hopeful', 35: 'harmful', 36: 'painful', 37: 'joyful', 38: 'fearful', 39: 'wishful', 40: 'peaceful'
      }
    },
    year5: {
      name: 'Year 5',
      description: 'Advanced vocabulary and prefixes',
      words: {
        1: 'replay', 2: 'rewrite', 3: 'remake', 4: 'retell', 5: 'return', 6: 'review', 7: 'rebuild', 8: 'repair', 9: 'replace', 10: 'refresh',
        11: 'unhappy', 12: 'unlock', 13: 'unfair', 14: 'unsafe', 15: 'unwell', 16: 'unknown', 17: 'unpack', 18: 'untrue', 19: 'unable', 20: 'unwise',
        21: 'disappear', 22: 'disagree', 23: 'dislike', 24: 'discover', 25: 'discuss', 26: 'display', 27: 'disturb', 28: 'dismiss', 29: 'dissolve', 30: 'distant',
        31: 'misread', 32: 'mistake', 33: 'mislead', 34: 'misplace', 35: 'mishear', 36: 'miscount', 37: 'misspell', 38: 'mistrust', 39: 'misuse', 40: 'misbehave'
      }
    },
    year6: {
      name: 'Year 6',
      description: 'Complex multi-syllable words',
      words: {
        1: 'incredible', 2: 'wonderful', 3: 'beautiful', 4: 'dangerous', 5: 'excellent', 6: 'different', 7: 'important', 8: 'interesting', 9: 'powerful', 10: 'comfortable',
        11: 'adventure', 12: 'experience', 13: 'mystery', 14: 'discovery', 15: 'invention', 16: 'exploration', 17: 'education', 18: 'celebration', 19: 'imagination', 20: 'information',
        21: 'encyclopedia', 22: 'photography', 23: 'geography', 24: 'biography', 25: 'technology', 26: 'democracy', 27: 'vocabulary', 28: 'astronomy', 29: 'psychology', 30: 'philosophy',
        31: 'responsibility', 32: 'understanding', 33: 'communicate', 34: 'participate', 35: 'appreciate', 36: 'investigate', 37: 'demonstrate', 38: 'concentrate', 39: 'accommodate', 40: 'congratulate'
      }
    }
  };
  
  // Get current word list based on difficulty
  const getCurrentWordList = () => {
    return difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.words || difficultyLevels.kindergarten.words;
  };
  
  // Traditional dice face with dots
  const renderDiceFace = (value: number) => {
    const dotPositions = {
      1: ['center'],
      2: ['top-left', 'bottom-right'],
      3: ['top-left', 'center', 'bottom-right'],
      4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'],
      6: ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right']
    };

    const positions = dotPositions[value as keyof typeof dotPositions] || [];

    return (
      <div className="relative w-full h-full">
        {positions.map((position, index) => (
          <div
            key={index}
            className={`absolute w-2 h-2 lg:w-3 lg:h-3 bg-[#2d5a35] rounded-full ${
              position === 'top-left' ? 'top-1 left-1 lg:top-2 lg:left-2' :
              position === 'top-right' ? 'top-1 right-1 lg:top-2 lg:right-2' :
              position === 'middle-left' ? 'top-1/2 left-1 lg:left-2 transform -translate-y-1/2' :
              position === 'middle-right' ? 'top-1/2 right-1 lg:right-2 transform -translate-y-1/2' :
              position === 'center' ? 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' :
              position === 'bottom-left' ? 'bottom-1 left-1 lg:bottom-2 lg:left-2' :
              position === 'bottom-right' ? 'bottom-1 right-1 lg:bottom-2 lg:right-2' : ''
            }`}
          />
        ))}
      </div>
    );
  };

  // Traditional dice component with enhanced design
  const renderDice = () => {
    return (
      <div className="relative w-16 h-16 lg:w-20 lg:h-20 mx-auto">
        <div 
          className={`w-full h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 border-gray-400 rounded-xl shadow-2xl transition-transform duration-200 ${
            gameState.isRolling ? 'animate-spin' : ''
          }`}
          style={{
            boxShadow: '0 8px 20px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.8), inset 0 -2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div className="w-full h-full rounded-xl bg-gradient-to-br from-white to-gray-50 border border-gray-200">
            {renderDiceFace(gameState.diceValue)}
          </div>
        </div>
      </div>
    );
  };

  // Pastel rainbow colors for squares
  const getSquareColor = (position: number) => {
    const colors = [
      'bg-red-100', 'bg-pink-100', 'bg-purple-100', 'bg-indigo-100', 'bg-blue-100',
      'bg-cyan-100', 'bg-teal-100', 'bg-green-100', 'bg-lime-100', 'bg-yellow-100',
      'bg-amber-100', 'bg-orange-100'
    ];
    return colors[(position - 1) % colors.length];
  };
  
  const generateGameId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };
  
  const joinGame = () => {
    if (!playerName.trim()) return;
    
    const newGameId = gameId || generateGameId();
    const playerId = Math.random().toString(36).substring(2, 15);
    const colorIndex = gameState.players.length % playerColors.length;
    
    const newPlayer: Player = {
      id: playerId,
      name: playerName.trim(),
      position: 0,
      color: playerColors[colorIndex].bg,
      isTeacher: isTeacher
    };
    
    setGameState(prev => ({
      ...prev,
      players: [...prev.players, newPlayer],
      gameId: newGameId,
      difficulty: selectedDifficulty
    }));
    
    setGameId(newGameId);
    setShowJoinModal(false);
    setIsConnected(true);
    
    // Show game code modal for teachers creating new games
    if (isTeacher && !gameId) {
      setShowGameCodeModal(true);
    }
  };
  
  const copyGameCode = async () => {
    try {
      await navigator.clipboard.writeText(gameState.gameId);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = gameState.gameId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };
  
  const rollDice = () => {
    if (gameState.isRolling || gameState.winner || !isConnected) return;
    
    setGameState(prev => ({ ...prev, isRolling: true }));
    const newDice = Math.floor(Math.random() * 6) + 1;
    
    setTimeout(() => {
      setGameState(prev => ({ ...prev, diceValue: newDice }));
      movePlayer(newDice);
      setGameState(prev => ({ ...prev, isRolling: false }));
    }, 1000);
  };
  
  const movePlayer = (steps: number) => {
    const newPlayers = [...gameState.players];
    const player = newPlayers[gameState.currentPlayer];
    let newPosition = player.position + steps;
    
    // Allow winning even with high rolls - cap at 40
    if (newPosition > 40) {
      newPosition = 40;
    }
    
    // Set moving player for animation
    setGameState(prev => ({ ...prev, movingPlayer: player.id }));
    
    // Show word for the new position
    const wordList = getCurrentWordList();
    if (newPosition > 0 && wordList[newPosition]) {
      setCurrentWord(wordList[newPosition]);
      setShowWordModal(true);
    }
    
    // First move to the rolled position
    player.position = newPosition;
    
    // Then check for snakes and ladders after a delay
    setTimeout(() => {
      let finalPosition = newPosition;
      let hasSpecialMove = false;
      
      if (snakes[newPosition as keyof typeof snakes]) {
        finalPosition = snakes[newPosition as keyof typeof snakes];
        hasSpecialMove = true;
      } else if (ladders[newPosition as keyof typeof ladders]) {
        finalPosition = ladders[newPosition as keyof typeof ladders];
        hasSpecialMove = true;
      }
      
      if (hasSpecialMove) {
        // Animate the snake/ladder movement
        setTimeout(() => {
          player.position = finalPosition;
          
          let winner = null;
          if (finalPosition === 40) { // Changed from 100 to 40
            winner = player;
            setShowCelebration(true);
            // Auto-hide celebration after 5 seconds
            setTimeout(() => setShowCelebration(false), 5000);
          }
          
          setGameState(prev => ({
            ...prev,
            players: newPlayers,
            currentPlayer: (prev.currentPlayer + 1) % newPlayers.length,
            winner,
            movingPlayer: null
          }));
        }, 1000);
      } else {
        let winner = null;
        if (finalPosition === 40) { // Changed from 100 to 40
          winner = player;
          setShowCelebration(true);
          // Auto-hide celebration after 5 seconds
          setTimeout(() => setShowCelebration(false), 5000);
        }
        
        setGameState(prev => ({
          ...prev,
          players: newPlayers,
          currentPlayer: (prev.currentPlayer + 1) % newPlayers.length,
          winner,
          movingPlayer: null
        }));
      }
    }, 500);
  };
  
  const resetGame = () => {
    setGameState(prev => ({
      ...prev,
      players: prev.players.map(p => ({ ...p, position: 0 })),
      currentPlayer: 0,
      diceValue: 1,
      winner: null,
      gameStarted: false,
      movingPlayer: null
    }));
    setCurrentWord('');
    setShowWordModal(false);
    setShowCelebration(false);
  };
  
  const closeWordModal = () => {
    setShowWordModal(false);
  };
  
  // Get board position coordinates for snake/ladder rendering
  const getPositionCoordinates = (position: number) => {
    const row = Math.floor((position - 1) / 5);
    const col = (row % 2 === 0) ? (position - 1) % 5 : 4 - ((position - 1) % 5);
    return { row: 7 - row, col };
  };
  
  const renderBoard = () => {
    const squares = [];
    // Fixed board layout for 40 squares (5x8 grid)
    for (let row = 7; row >= 0; row--) {
      for (let col = 0; col < 5; col++) {
        let position;
        if (row % 2 === 1) {
          // Odd rows: left to right
          position = row * 5 + col + 1;
        } else {
          // Even rows: right to left
          position = row * 5 + (4 - col) + 1;
        }
        
        if (position > 40) continue;
        
        const playersOnSquare = gameState.players.filter(p => p.position === position);
        const hasSnake = snakes[position as keyof typeof snakes];
        const hasLadder = ladders[position as keyof typeof ladders];
        const squareColor = getSquareColor(position);
        
        squares.push(
          <div
            key={position}
            className={`relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 border-2 flex items-center justify-center text-sm font-bold ${
              hasSnake ? 'bg-red-100 border-red-400' : 
              hasLadder ? 'bg-green-100 border-green-400' : 
              `${squareColor} border-gray-300`
            }`}
          >
            <span className={`${hasSnake ? 'text-red-800' : hasLadder ? 'text-green-800' : 'text-gray-700'} font-bold text-sm lg:text-base z-10`}>
              {position}
            </span>
            
            {/* Simple snake and ladder indicators */}
            {hasSnake && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl">🐍</span>
              </div>
            )}
            {hasLadder && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl">🪜</span>
              </div>
            )}
            
            {/* Enhanced game pieces with better visibility and fixed positioning */}
            {playersOnSquare.map((player, idx) => {
              const offsetX = (idx % 2) * 10 - 5;
              const offsetY = Math.floor(idx / 2) * 10 - 5;
              const isMoving = gameState.movingPlayer === player.id;
              
              return (
                <div
                  key={player.id}
                  className={`absolute w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 rounded-full ${player.color} border-3 border-white shadow-2xl flex items-center justify-center transform transition-all duration-500 ${
                    isMoving ? 'scale-125 animate-pulse z-50' : 'scale-100 z-50'
                  }`}
                  style={{
                    transform: `translate(${offsetX}px, ${offsetY}px) ${isMoving ? 'scale(1.25)' : 'scale(1)'}`,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.4)'
                  }}
                >
                  {player.isTeacher ? (
                    <i className="ri-graduation-cap-fill text-white text-sm sm:text-lg lg:text-xl font-bold drop-shadow-lg"></i>
                  ) : (
                    <i className="ri-user-fill text-white text-sm sm:text-lg lg:text-xl font-bold drop-shadow-lg"></i>
                  )}
                </div>
              );
            })}
          </div>
        );
      }
    }
    return squares;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      <NavBar />
      
      {/* Celebration Animation */}
      {showCelebration && gameState.winner && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] pointer-events-none">
          <div className="text-center animate-bounce">
            <div className="text-8xl mb-4 animate-pulse">🎉</div>
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-lg">
              <div className="text-6xl mb-4">🏆</div>
              <h2 className="text-4xl font-bold text-[#2d5a35] mb-2 animate-pulse">
                {gameState.winner.name} Wins!
              </h2>
              <p className="text-xl text-gray-600">Amazing reading skills!</p>
            </div>
          </div>
          {/* Confetti effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 50 }, (_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 bg-yellow-400 animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Join Game Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gamepad-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Join Phonics Game</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-xl text-center font-medium text-lg"
                />
              </div>
              
              {/* Only show game code input if not creating new game */}
              {!isTeacher && (
                <div>
                  <input
                    type="text"
                    placeholder="Enter Game Code to Join"
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value.toUpperCase())}
                    className="w-full p-4 border border-gray-300 rounded-xl text-center font-mono text-lg"
                  />
                </div>
              )}
              
              {/* Difficulty Selection - only for teachers creating games */}
              {isTeacher && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Choose Difficulty Level</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(difficultyLevels).map(([key, level]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedDifficulty(key)}
                        className={`p-3 rounded-xl border-2 text-left transition-colors ${
                          selectedDifficulty === key
                            ? 'border-[#2d5a35] bg-green-50 text-[#2d5a35]'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{level.name}</div>
                        <div className="text-xs text-gray-500">{level.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isTeacher}
                    onChange={(e) => setIsTeacher(e.target.checked)}
                    className="w-5 h-5 text-[#2d5a35] rounded"
                  />
                  <span className="text-gray-700">I'm a teacher</span>
                </label>
              </div>
              
              <Button onClick={joinGame} disabled={!playerName.trim()} className="w-full py-4 text-lg">
                {isTeacher ? 'Create New Game' : gameId ? 'Join Game' : 'Enter Game Code or Check Teacher Box'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Game Code Share Modal */}
      {showGameCodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Share Game Code</h3>
              <p className="text-gray-600 mt-2">Send this code to your students</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#2d5a35] rounded-2xl p-8 text-center">
                <div className="text-4xl font-bold text-white font-mono tracking-wider mb-2">
                  {gameState.gameId}
                </div>
                <p className="text-green-100">Game Code</p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-lg font-medium text-[#2d5a35] mb-1">
                  {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.name}
                </div>
                <div className="text-sm text-gray-600">
                  {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.description}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={copyGameCode} variant="outline" className="flex-1 py-3">
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy Code
                </Button>
                <Button onClick={() => setShowGameCodeModal(false)} className="flex-1 py-3">
                  Got It!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Word Reading Modal */}
      {showWordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 text-center max-w-lg w-full shadow-2xl">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-open-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Read This Word!</h3>
            <div className="text-5xl md:text-6xl font-bold text-[#2d5a35] mb-6 py-6 bg-green-50 rounded-2xl">
              {currentWord}
            </div>
            <p className="text-lg text-gray-600 mb-6">Great job reading!</p>
            <Button onClick={closeWordModal} className="w-full py-4 text-lg">
              Continue
            </Button>
          </div>
        </div>
      )}
      
      <div className="pt-24 md:pt-28 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate('/games')} className="w-10 h-10 flex items-center justify-center">
              <i className="ri-arrow-left-line text-[#2d5a35] text-xl"></i>
            </button>
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800">Phonics Snakes and Ladders</h2>
              {isConnected && (
                <div className="flex items-center justify-center space-x-2 mt-2">
                  <p className="text-[#2d5a35] font-mono font-bold text-lg">{gameState.gameId}</p>
                  <button 
                    onClick={() => setShowGameCodeModal(true)}
                    className="w-8 h-8 flex items-center justify-center text-[#2d5a35] hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              )}
            </div>
            <img 
              src="https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/4a99c4ae32eb70e334047e2a84452586.png" 
              alt="Sprout mascot" 
              className="w-10 h-10 opacity-60"
            />
          </div>
          
          <div className="flex flex-col xl:flex-row gap-8">
            {/* Game Board - Takes most space on desktop */}
            <div className="xl:flex-1">
              {gameState.winner && (
                <div className="bg-gradient-to-r from-[#2d5a35] to-green-600 rounded-2xl p-6 mb-8 text-center">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-trophy-line text-[#2d5a35] text-3xl"></i>
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-2">🎉 {gameState.winner.name} Wins! 🎉</h3>
                  <p className="text-green-100">Amazing reading skills!</p>
                </div>
              )}
              
              {/* Enhanced game board optimized for desktop */}
              <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-12 shadow-lg border-2 border-green-100">
                <div className="relative grid grid-cols-5 gap-1 sm:gap-2 lg:gap-3 xl:gap-4 mb-6 max-w-none mx-auto">
                  {renderBoard()}
                </div>
                
                {/* Legend for snakes and ladders */}
                <div className="flex justify-center space-x-8 mt-6 text-sm md:text-base lg:text-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-4xl">🐍</span>
                    <span className="text-red-600 font-medium">Slide Down</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-4xl">🪜</span>
                    <span className="text-green-600 font-medium">Climb Up</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Game Controls & Players - Sidebar on desktop */}
            <div className="xl:w-80 space-y-6">
              {isConnected && gameState.players.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-center">
                      {renderDice()}
                      <p className="text-sm text-gray-600 font-medium mt-2">Dice</p>
                    </div>
                    
                    <div className="text-center">
                      <div className={`w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-white shadow-lg ${gameState.players[gameState.currentPlayer]?.color || 'bg-gray-500'}`}>
                        {gameState.players[gameState.currentPlayer]?.isTeacher ? (
                          <i className="ri-graduation-cap-fill text-white text-xl lg:text-2xl"></i>
                        ) : (
                          <i className="ri-user-fill text-white text-xl lg:text-2xl"></i>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 font-medium">{gameState.players[gameState.currentPlayer]?.name}</p>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={gameState.gameStarted ? rollDice : () => setGameState(prev => ({ ...prev, gameStarted: true }))}
                    disabled={gameState.isRolling || !!gameState.winner}
                    className="w-full mb-4 py-3 text-lg"
                  >
                    {!gameState.gameStarted ? 'Start Phonics Game' : gameState.isRolling ? 'Rolling...' : gameState.winner ? 'Game Over' : 'Roll Dice'}
                  </Button>
                  
                  {gameState.winner && (
                    <Button 
                      onClick={resetGame}
                      variant="outline"
                      className="w-full py-3"
                    >
                      Play Again
                    </Button>
                  )}
                </div>
              )}
              
              {isConnected && gameState.players.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                  <h3 className="font-bold text-gray-800 mb-4 text-lg">Players & Progress</h3>
                  
                  {/* Difficulty indicator */}
                  <div className="bg-green-50 rounded-xl p-3 mb-4">
                    <div className="text-center">
                      <div className="font-bold text-[#2d5a35]">
                        {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.name}
                      </div>
                      <div className="text-xs text-gray-600">
                        {difficultyLevels[gameState.difficulty as keyof typeof difficultyLevels]?.description}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {gameState.players.map((player) => (
                      <div key={player.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center border-2 border-white shadow-lg ${player.color}`}>
                            {player.isTeacher ? (
                              <i className="ri-graduation-cap-fill text-white text-lg"></i>
                            ) : (
                              <i className="ri-user-fill text-white text-lg"></i>
                            )}
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">{player.name}</span>
                            {player.isTeacher && (
                              <span className="ml-2 text-xs bg-[#2d5a35] text-white px-2 py-1 rounded-full">Teacher</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-800">Square: {player.position}</div>
                          <div className="text-sm text-[#2d5a35]">{40 - player.position} to finish</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-[#2d5a35] font-medium">
                      📚 Read the word on each square you land on to practice your phonics skills! First to reach square 40 wins!
                    </p>
                  </div>
                </div>
              )}
              
              {!isConnected && (
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-green-100">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-wifi-off-line text-gray-400 text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Join a Game</h3>
                  <p className="text-gray-600 mb-4">Connect with other players to start playing!</p>
                  <Button onClick={() => setShowJoinModal(true)} className="w-full py-3">
                    Join Game
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
